import { memo, useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import type { Components } from "react-markdown";
import { cn } from "../../lib/utils";

interface MarkdownContentProps {
  content: string;
  className?: string;
}

function CopyButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setCopied(true);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (non-HTTPS, denied permission)
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? "Copied to clipboard" : "Copy code to clipboard"}
      className="text-[11px] font-medium text-ink-faint transition-colors hover:text-white"
    >
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

const components: Components = {
  h1: ({ children }) => (
    <h1 className="mb-3 mt-5 text-lg font-bold text-ink first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-4 text-base font-bold text-ink first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-2 mt-3 text-sm font-semibold text-ink first:mt-0">
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p className="mb-3 text-sm leading-relaxed text-ink last:mb-0">
      {children}
    </p>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline decoration-primary/40 underline-offset-2 transition-colors hover:decoration-primary"
    >
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul className="mb-3 ml-5 list-disc space-y-1 text-sm text-ink last:mb-0">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-3 ml-5 list-decimal space-y-1 text-sm text-ink last:mb-0">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="text-sm leading-relaxed text-ink">
      {children}
    </li>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-3 border-l-3 border-primary/40 pl-4 italic text-ink-soft last:mb-0">
      {children}
    </blockquote>
  ),
  code: ({ className, children }) => {
    const isBlock =
      /language-\w+/.test(className ?? "") || String(children).includes("\n");
    const language = className?.replace("language-", "") ?? "";
    const codeString = String(children).replace(/\n$/, "");

    if (isBlock) {
      return (
        <div className="group mb-3 overflow-hidden rounded-xl border border-black/[0.08] last:mb-0">
          {language && (
            <div className="flex items-center justify-between bg-ink px-4 py-2">
              <span className="text-[11px] font-medium text-ink-faint">
                {language}
              </span>
              <CopyButton code={codeString} />
            </div>
          )}
          {!language && (
            <div className="flex items-center justify-end bg-ink px-4 py-2">
              <CopyButton code={codeString} />
            </div>
          )}
          <pre className="overflow-x-auto bg-ink p-4">
            <code className="text-xs leading-relaxed text-white">
              {codeString}
            </code>
          </pre>
        </div>
      );
    }

    return (
      <code className="rounded-md bg-surface-3 px-1.5 py-0.5 text-[13px] font-medium text-ink">
        {children}
      </code>
    );
  },
  // pre is handled by the code override for block code
  pre: ({ children }) => <>{children}</>,
  table: ({ children }) => (
    <div className="mb-3 overflow-x-auto last:mb-0">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="border-b border-black/[0.08] bg-surface-2">
      {children}
    </thead>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 text-left text-xs font-semibold text-ink-muted">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-black/[0.06] px-3 py-2 text-ink">
      {children}
    </td>
  ),
  hr: () => <hr className="my-4 border-black/[0.08]" />,
  strong: ({ children }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
};

const remarkPlugins = [remarkGfm];
const rehypePlugins = [rehypeSanitize];

export const MarkdownContent = memo(function MarkdownContent({
  content,
  className,
}: MarkdownContentProps) {
  return (
    <div className={cn("min-w-0", className)}>
      <Markdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {content}
      </Markdown>
    </div>
  );
});
